<template>
  <registry-page
    title-id="warehouse-directory-title"
    title="Склады"
    description="Места хранения, используемые в складских операциях и отчетах."
  >
    <template #actions>
      <q-btn
        v-if="canEdit"
        ref="addButton"
        color="primary"
        icon="add"
        label="Добавить склад"
        data-testid="warehouse-directory-add"
        :disable="pending"
        @click="openCreate"
      />
    </template>

    <app-data-table
      data-testid="warehouse-directory-table"
      title="Справочник складов"
      :count="visibleRows.length"
      :rows="visibleRows"
      :columns="columns"
      :pagination="pagination"
      :state="tableState"
      :state-title="stateTitle"
      :state-message="errorMessage"
      storage-key="warehouse-directory"
      row-key="id"
      wrap-cells
      @reset="resetFilters"
      @retry="loadRows"
      @row-click="openEdit"
    >
      <template #filters>
        <filter-bar
          v-model="search"
          search-label="Поиск по коду, названию или описанию"
          search-test-id="warehouse-directory-search"
          :has-filters="hasFilters"
          :applied-filters="appliedFilters"
          @search="applySearch"
          @remove="removeFilter"
          @reset="resetFilters"
        >
          <template #quick>
            <q-select
              :model-value="statusFilter"
              data-testid="warehouse-directory-status"
              dense outlined emit-value map-options label="Статус"
              :options="statusOptions"
              @update:model-value="applyStatus"
            />
          </template>
          <template #advanced>
            <q-select
              :model-value="statusFilter"
              dense outlined emit-value map-options label="Статус"
              :options="statusOptions"
              @update:model-value="applyStatus"
            />
          </template>
        </filter-bar>
      </template>
      <template #body-cell-is_active="props">
        <q-td :props="props">
          <status-badge :tone="props.value === false ? 'neutral' : 'success'" :label="props.value === false ? 'Архив' : 'Активен'" />
        </q-td>
      </template>
      <template #body-cell-actions="props">
        <q-td :props="props">
          <div v-if="canEdit && props.row.is_active !== false" class="row no-wrap justify-end q-gutter-xs">
            <q-btn
              flat round dense color="primary" icon="edit"
              aria-label="Редактировать склад"
              data-testid="warehouse-directory-edit"
              :disable="pending"
              @click.stop="openEdit(null, props.row)"
            />
            <q-btn
              flat round dense color="negative" icon="archive"
              aria-label="Архивировать склад"
              data-testid="warehouse-directory-archive"
              :disable="pending"
              @click.stop="openArchive(props.row)"
            />
          </div>
        </q-td>
      </template>
    </app-data-table>

    <q-dialog v-model="formDialog" persistent @hide="restoreFormFocus">
      <q-card class="warehouse-dialog">
        <q-card-section><h2 class="warehouse-dialog__title">{{ editMode ? 'Изменить склад' : 'Добавить склад' }}</h2></q-card-section>
        <q-card-section class="q-gutter-md q-pt-none">
          <q-banner v-if="formError" rounded class="bg-red-1 text-negative" data-testid="warehouse-directory-form-error">{{ formError }}</q-banner>
          <q-input ref="codeInput" v-model.trim="form.code" outlined dense label="Код" data-testid="warehouse-directory-code" :disable="pending" />
          <q-input v-model.trim="form.name" outlined dense label="Название" data-testid="warehouse-directory-name" :disable="pending" />
          <q-input v-model.trim="form.description" outlined dense autogrow label="Описание" data-testid="warehouse-directory-description" :disable="pending" />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="primary" :disable="pending" @click="formDialog = false" />
          <q-btn
            color="primary"
            :label="editMode ? 'Сохранить' : 'Добавить'"
            data-testid="warehouse-directory-save"
            :disable="pending"
            :loading="pending"
            @click="save"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <confirm-dialog
      v-model="archiveDialog"
      title="Архивировать склад?"
      :body="archiveDialogBody"
      confirm-label="Архивировать"
      tone="danger"
      :pending="pending"
      @confirm="archiveSelected"
    />
  </registry-page>
</template>

<script>
import {
  AppDataTable,
  ConfirmDialog,
  FilterBar,
  RegistryPage,
  StatusBadge
} from '../../components/ui'
import {
  createWarehouseBrowseAdapter,
  isWarehouseError,
  normalizeWarehouseRows,
  warehouseColumns,
  warehouseError
} from '../../services/warehouseBrowse'

function emptyForm() {
  return { id: null, code: '', name: '', description: '' }
}

export default {
  name: 'WarehouseDirectory',
  components: { AppDataTable, ConfirmDialog, FilterBar, RegistryPage, StatusBadge },
  data() {
    const query = this.$route.query
    return {
      adapter: null,
      rows: [],
      loading: true,
      pending: false,
      errorMessage: '',
      accessDenied: false,
      search: String(query.q || ''),
      statusFilter: String(query.status || 'active'),
      statusOptions: [
        { label: 'Активные', value: 'active' },
        { label: 'Архивные', value: 'archived' },
        { label: 'Все', value: 'all' }
      ],
      columns: warehouseColumns,
      pagination: { page: 1, rowsPerPage: 20, rowsNumber: 0 },
      requestId: null,
      mutationRequestId: null,
      handlers: [],
      formDialog: false,
      archiveDialog: false,
      editMode: false,
      selectedRow: null,
      form: emptyForm(),
      formError: '',
      formReturnFocus: null
    }
  },
  computed: {
    permissions() {
      return this.$store.state.permissions || {}
    },
    canView() {
      return this.permissions.open_stock === true || this.permissions.edit_stock === true
    },
    canEdit() {
      return this.permissions.edit_stock === true
    },
    hasFilters() {
      return Boolean(this.search || this.statusFilter !== 'active')
    },
    appliedFilters() {
      const filters = []
      if (this.search) filters.push({ key: 'search', label: `Поиск: ${this.search}` })
      if (this.statusFilter !== 'active') {
        const option = this.statusOptions.find((item) => item.value === this.statusFilter)
        filters.push({ key: 'status', label: `Статус: ${option ? option.label : this.statusFilter}` })
      }
      return filters
    },
    visibleRows() {
      const query = this.search.trim().toLocaleLowerCase('ru')
      return this.rows.filter((row) => {
        const active = row.is_active !== false
        if (this.statusFilter === 'active' && !active) return false
        if (this.statusFilter === 'archived' && active) return false
        if (!query) return true
        return [row.code, row.name, row.description]
          .some((value) => String(value || '').toLocaleLowerCase('ru').includes(query))
      })
    },
    tableState() {
      if (!this.canView || this.accessDenied) return 'access-denied'
      if (this.errorMessage) return 'error'
      if (this.loading && this.rows.length === 0) return 'loading'
      if (this.visibleRows.length === 0) return this.hasFilters ? 'filtered-empty' : 'empty'
      return 'ready'
    },
    stateTitle() {
      if (this.tableState === 'filtered-empty') return 'Склады по выбранным условиям не найдены'
      if (this.tableState === 'error') return 'Справочник складов не загрузился'
      return ''
    },
    archiveDialogBody() {
      const row = this.selectedRow || {}
      return `«${row.name || 'Без названия'}» останется в истории операций, но будет скрыт из активного справочника.`
    }
  },
  watch: {
    '$route.query': {
      deep: true,
      handler(query) {
        this.search = String(query.q || '')
        this.statusFilter = String(query.status || 'active')
      }
    }
  },
  mounted() {
    this.adapter = createWarehouseBrowseAdapter(this.connection)
    this.bind('onListWarehouses', this.onRows)
    this.bind('onCreateWarehouse', this.onMutation)
    this.bind('onUpdateWarehouse', this.onMutation)
    this.bind('onArchiveWarehouse', this.onMutation)
    this.bind('accessDeniedErrorReceived', this.onAccessDenied)
    const ready = () => this.loadRows()
    this.handlers.push(['connectionReady', ready])
    if (this.connection.isAuthenticated) ready()
    else this.emitter.on('connectionReady', ready)
  },
  beforeUnmount() {
    this.handlers.forEach(([name, handler]) => this.emitter.off(name, handler))
  },
  methods: {
    bind(name, handler) {
      this.handlers.push([name, handler])
      this.emitter.on(name, handler)
    },
    pushQuery(changes) {
      const query = { ...this.$route.query, ...changes }
      Object.keys(query).forEach((key) => {
        if (query[key] === null || query[key] === undefined || query[key] === '') delete query[key]
      })
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) this.$router.push({ query })
    },
    applySearch(value) { this.pushQuery({ q: value || null }) },
    applyStatus(value) { this.pushQuery({ status: value === 'active' ? null : value }) },
    removeFilter(key) {
      if (key === 'search') this.applySearch('')
      if (key === 'status') this.applyStatus('active')
    },
    resetFilters() { this.pushQuery({ q: null, status: null }) },
    loadRows() {
      if (!this.adapter) return
      this.loading = true
      this.errorMessage = ''
      this.accessDenied = false
      this.requestId = this.adapter.warehouses(true)
    },
    onRows(response) {
      if (response.request_id !== this.requestId) return
      this.loading = false
      if (isWarehouseError(response)) {
        this.accessDenied = response.error === 'access_denied'
        this.errorMessage = this.accessDenied ? '' : warehouseError(response, 'Не удалось загрузить склады')
        return
      }
      this.rows = normalizeWarehouseRows(response)
      this.pagination = { ...this.pagination, rowsNumber: this.rows.length }
    },
    onAccessDenied(response) {
      if (response.request_id === this.mutationRequestId) {
        this.pending = false
        this.formError = 'Недостаточно прав для изменения справочника складов'
        return
      }
      if (response.request_id !== this.requestId) return
      this.loading = false
      this.accessDenied = true
      this.errorMessage = ''
    },
    openCreate() {
      if (!this.canEdit || this.pending) return
      this.formReturnFocus = (this.$refs.addButton && this.$refs.addButton.$el) || document.activeElement
      this.editMode = false
      this.selectedRow = null
      this.form = emptyForm()
      this.formError = ''
      this.formDialog = true
      this.$nextTick(() => this.$refs.codeInput && this.$refs.codeInput.focus())
    },
    openEdit(event, row) {
      if (!this.canEdit || this.pending || !row || row.is_active === false) return
      this.formReturnFocus = document.activeElement
      this.editMode = true
      this.selectedRow = row
      this.form = { id: row.id, code: row.code || '', name: row.name || '', description: row.description || '' }
      this.formError = ''
      this.formDialog = true
      this.$nextTick(() => this.$refs.codeInput && this.$refs.codeInput.focus())
    },
    restoreFormFocus() {
      const target = this.formReturnFocus
      this.$nextTick(() => target && typeof target.focus === 'function' && target.focus())
    },
    save() {
      if (!this.canEdit || this.pending) return
      if (!this.form.code || !this.form.name) {
        this.formError = 'Заполните код и название склада'
        this.$nextTick(() => this.$refs.codeInput && this.$refs.codeInput.focus())
        return
      }
      this.formError = ''
      this.pending = true
      const payload = { code: this.form.code, name: this.form.name, description: this.form.description }
      this.mutationRequestId = this.editMode
        ? this.adapter.updateWarehouse(this.form.id, payload)
        : this.adapter.createWarehouse(payload)
    },
    openArchive(row) {
      if (!this.canEdit || this.pending || !row) return
      this.selectedRow = row
      this.archiveDialog = true
    },
    archiveSelected() {
      if (!this.canEdit || this.pending || !this.selectedRow) return
      this.pending = true
      this.mutationRequestId = this.adapter.archiveWarehouse(this.selectedRow.id)
    },
    onMutation(response) {
      if (response.request_id !== this.mutationRequestId) return
      this.pending = false
      if (isWarehouseError(response)) {
        const message = warehouseError(response, 'Не удалось сохранить склад')
        if (this.formDialog) this.formError = message
        else this.errorMessage = message
        return
      }
      this.formDialog = false
      this.archiveDialog = false
      this.formError = ''
      this.errorMessage = ''
      this.loadRows()
    }
  }
}
</script>

<style scoped>
.warehouse-dialog { width: min(32rem, 92vw); }
.warehouse-dialog__title { margin: 0; font-size: var(--sid-font-size-section); line-height: var(--sid-line-height-section); }
:deep(.app-data-table) { min-width: 0; }
:deep(.q-table__middle) { max-width: 100%; overflow: auto; }
</style>
